import { useEffect, useRef, useState } from "preact/hooks"
import { useMediaRecorder } from "../hooks/useMediaRecorder"
import { api } from "../lib/api"
import {
	type PhotoEdits,
	defaultEdits,
	isNoopEdits,
	renderEditedImage,
} from "../lib/imageEdits"
import { todayArtDate } from "../lib/time"
import type {
	ArtDate,
	BoxDetail,
	NewAssetInput,
	ResourceType,
} from "../lib/types"
import { uploadToCloudinary, type UploadedAsset } from "../lib/uploads"
import { ArtDateInput } from "./ArtDateInput"
import { PhotoEditor } from "./PhotoEditor"
import { Spinner } from "./Spinner"
import { useToast } from "./Toast"

interface AddArtworkProps {
	boxId: number
	navigate: (path: string) => void
}

interface PhotoItem {
	file: File
	url: string
	uploaded: UploadedAsset | null
	uploading: boolean
	uploadError: string | null
	// Edits are kept against the original `file` and re-applied from it on
	// every change (see lib/imageEdits.ts), so re-editing never stacks JPEG
	// re-encodes. When edits are active, `editedBlob` is the full-res render
	// that gets uploaded instead of `file`, and `editedUrl` previews it.
	edits: PhotoEdits
	editedBlob?: Blob
	editedUrl?: string
	// Photos upload eagerly on pick, so an edit invalidates whatever was
	// already uploaded. `uploadToken` is bumped per (re-)upload: a slower
	// in-flight upload whose token no longer matches lost the race, and its
	// result is discarded rather than clobbering the newer edit.
	uploadToken: number
}

interface RecordingItem {
	file?: File
	blob?: Blob
	url: string
	kind: "audio" | "video"
	uploaded: UploadedAsset | null
	uploading: boolean
	uploadError: string | null
}

// Binds a live camera stream to a <video> preview — srcObject can't be set
// via a JSX attribute, so this needs an effect + ref.
function LiveVideo({ stream }: { stream: MediaStream | null }) {
	const ref = useRef<HTMLVideoElement>(null)
	useEffect(() => {
		if (ref.current) ref.current.srcObject = stream
	}, [stream])
	return (
		<video
			ref={ref}
			autoPlay
			playsInline
			muted
			class="w-100 rounded bg-dark mb-2"
			style={{ maxHeight: "40vh" }}
		/>
	)
}

export function AddArtwork({ boxId, navigate }: AddArtworkProps) {
	const [box, setBox] = useState<BoxDetail | null>(null)
	const [loadError, setLoadError] = useState("")

	const [photos, setPhotos] = useState<PhotoItem[]>([])
	const [recording, setRecording] = useState<RecordingItem | null>(null)
	const [recordingKind, setRecordingKind] = useState<"audio" | "video" | null>(
		null,
	)
	const [title, setTitle] = useState("")
	const [note, setNote] = useState("")
	const [madeDate, setMadeDate] = useState<ArtDate>(todayArtDate())
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState("")
	const [editingIdx, setEditingIdx] = useState<number | null>(null)

	const recorder = useMediaRecorder()
	const { toast } = useToast()
	const formTopRef = useRef<HTMLDivElement>(null)

	// Kept in sync with state on every render so the unmount-only cleanup
	// below can revoke whatever URLs are current without re-running (and
	// wrongly revoking still-in-use URLs) on every photos/recording change.
	const photosRef = useRef(photos)
	photosRef.current = photos
	const recordingRef = useRef(recording)
	recordingRef.current = recording

	useEffect(() => {
		api
			.getBox(boxId)
			.then(setBox)
			.catch((err) =>
				setLoadError(err instanceof Error ? err.message : "Failed to load box"),
			)
	}, [boxId])

	// Revoke object URLs for previews on unmount so we don't leak memory.
	// Also fire-and-forget Cloudinary cleanup for any eagerly uploaded assets
	// that were never saved (user navigated away).
	// `mountedRef` lets applyEdits notice it resolved after unmount (this
	// cleanup has already run, so any URL it creates then must be revoked
	// by applyEdits itself or it leaks for the page's lifetime).
	const mountedRef = useRef(true)
	// Set the moment createArtwork succeeds: from then on the uploads belong
	// to the saved artwork, and the unmount cleanup below must NOT destroy
	// them — it exists only for abandoned uploads. Without this guard the
	// navigate() right after saving destroyed every just-saved Cloudinary
	// asset, leaving artworks with DB rows pointing at dead files.
	const savedRef = useRef(false)
	useEffect(() => {
		return () => {
			mountedRef.current = false
			if (savedRef.current) {
				for (const p of photosRef.current) {
					URL.revokeObjectURL(p.url)
					if (p.editedUrl) URL.revokeObjectURL(p.editedUrl)
				}
				if (recordingRef.current) URL.revokeObjectURL(recordingRef.current.url)
				return
			}
			for (const p of photosRef.current) {
				URL.revokeObjectURL(p.url)
				if (p.editedUrl) URL.revokeObjectURL(p.editedUrl)
				if (p.uploaded) {
					api
						.destroyUpload(
							boxId,
							p.uploaded.cloudinary_public_id,
							p.uploaded.resource_type,
						)
						.catch(() => {})
				}
			}
			if (recordingRef.current) {
				URL.revokeObjectURL(recordingRef.current.url)
				if (recordingRef.current.uploaded) {
					api
						.destroyUpload(
							boxId,
							recordingRef.current.uploaded.cloudinary_public_id,
							recordingRef.current.uploaded.resource_type,
						)
						.catch(() => {})
				}
			}
		}
	}, [boxId])

	// Once the recorder produces a finished blob, promote it into `recording`.
	useEffect(() => {
		if (recorder.blob && recordingKind) {
			const blob = recorder.blob
			const kind = recordingKind
			setRecording({
				blob,
				url: URL.createObjectURL(blob),
				kind,
				uploaded: null,
				uploading: true,
				uploadError: null,
			})
			setRecordingKind(null)
			recorder.reset()
			// Eagerly upload the recording.
			uploadRecording(blob, kind)
		}
		// Only re-run when a new blob arrives — recorder/recordingKind are
		// read from the latest closure, and recorder.reset() would otherwise
		// re-trigger this effect in a loop if listed as a dependency.
		// biome-ignore lint/correctness/useExhaustiveDependencies: see above
	}, [recorder.blob])

	if (loadError) {
		return (
			<div class="container py-4">
				<div class="alert alert-danger">{loadError}</div>
			</div>
		)
	}
	if (!box) {
		return (
			<div class="container py-4">
				<Spinner />
			</div>
		)
	}
	if (box.role !== "owner") {
		return (
			<div class="container py-4">
				<div class="alert alert-warning">Only box owners can add art.</div>
				<a href={`/boxes/${boxId}`} class="btn btn-outline-secondary">
					Back to box
				</a>
			</div>
		)
	}

	// Upload a single photo (the original file, or an edited render) to
	// Cloudinary, updating its state entry by `url` — a stable per-photo key,
	// since editing replaces the array entry with a new object. `token` must
	// still match the entry's `uploadToken` on completion: if it doesn't, a
	// newer edit superseded this upload and the result is thrown away.
	const uploadPhoto = async (
		source: Blob | File,
		url: string,
		token: number,
	) => {
		try {
			const sign = await api.signUpload(boxId, "image")
			const uploaded = await uploadToCloudinary(source, sign, "image")
			let superseded = false
			setPhotos((prev) => {
				const idx = prev.findIndex((p) => p.url === url)
				if (idx === -1 || prev[idx].uploadToken !== token) {
					// Photo was removed, or a newer edit is already uploading.
					superseded = true
					return prev
				}
				const copy = [...prev]
				copy[idx] = { ...copy[idx], uploaded, uploading: false }
				return copy
			})
			// Destroy outside the updater — it must stay free of side effects.
			if (superseded) {
				api
					.destroyUpload(boxId, uploaded.cloudinary_public_id, "image")
					.catch(() => {})
			}
		} catch (err) {
			setPhotos((prev) => {
				const idx = prev.findIndex((p) => p.url === url)
				if (idx === -1 || prev[idx].uploadToken !== token) return prev
				const copy = [...prev]
				copy[idx] = {
					...copy[idx],
					uploading: false,
					uploadError: err instanceof Error ? err.message : "Upload failed",
				}
				return copy
			})
		}
	}

	// Upload a recording blob/file to Cloudinary.
	const uploadRecording = async (
		source: Blob | File,
		kind: "audio" | "video",
	) => {
		// Audio goes up under Cloudinary's "video" resource type too — that's
		// the one that accepts sound files; the "image" endpoint rejects them.
		const resourceType: ResourceType = "video"
		try {
			const sign = await api.signUpload(boxId, resourceType, kind)
			const uploaded = await uploadToCloudinary(source, sign, resourceType)
			setRecording((prev) => {
				if (!prev) return prev
				return { ...prev, uploaded, uploading: false, kind }
			})
		} catch (err) {
			setRecording((prev) => {
				if (!prev) return prev
				return {
					...prev,
					uploading: false,
					uploadError: err instanceof Error ? err.message : "Upload failed",
				}
			})
		}
	}

	const onPhotoInput = (e: Event) => {
		const input = e.target as HTMLInputElement
		const files = Array.from(input.files ?? [])
		const newPhotos: PhotoItem[] = files.map((file) => ({
			file,
			url: URL.createObjectURL(file),
			uploaded: null,
			uploading: true,
			uploadError: null,
			edits: defaultEdits(),
			uploadToken: 0,
		}))
		setPhotos((prev) => [...prev, ...newPhotos])
		// Eagerly upload each new photo. Editing one later re-uploads it.
		for (const p of newPhotos) {
			uploadPhoto(p.file, p.url, p.uploadToken)
		}
		input.value = ""
	}

	const removePhoto = (idx: number) => {
		// Capture the removed item so we can clean up outside the setter.
		let removed: PhotoItem | undefined
		setPhotos((prev) => {
			const copy = [...prev]
			;[removed] = copy.splice(idx, 1)
			return copy
		})
		if (removed) {
			URL.revokeObjectURL(removed.url)
			if (removed.editedUrl) URL.revokeObjectURL(removed.editedUrl)
			if (removed.uploaded) {
				api
					.destroyUpload(
						boxId,
						removed.uploaded.cloudinary_public_id,
						removed.uploaded.resource_type,
					)
					.catch((err) =>
						console.warn("Failed to clean up Cloudinary asset:", err),
					)
			}
		}
	}

	const applyEdits = async (item: PhotoItem, edits: PhotoEdits) => {
		setEditingIdx(null)
		try {
			// Render at full resolution now so the re-upload sends the final
			// pixels. A no-op edit reverts to uploading the original file.
			const rendered = isNoopEdits(edits)
				? null
				: await renderEditedImage(item.file, edits)
			const editedUrl = rendered
				? URL.createObjectURL(rendered.blob)
				: undefined
			// The photo may have been removed — or the page left — while the
			// async render was in flight. Look it up by its original preview
			// URL, a stable per-photo key: object identity would break here,
			// since an earlier apply on the same photo replaces the array
			// entry with a new spread object.
			const current = mountedRef.current
				? photosRef.current.find((p) => p.url === item.url)
				: undefined
			if (!current) {
				if (editedUrl) URL.revokeObjectURL(editedUrl)
				return
			}
			// Revoke the previous edit's URL from the live entry, not from
			// `item` — `item.editedUrl` is a stale snapshot from when the
			// editor was opened.
			if (current.editedUrl && current.editedUrl !== editedUrl) {
				URL.revokeObjectURL(current.editedUrl)
			}
			// Whatever was uploaded eagerly is now the wrong pixels: mark the
			// entry as uploading again under a fresh token, re-upload the
			// render, and destroy the stale asset so it doesn't orphan.
			const staleUpload = current.uploaded
			const token = current.uploadToken + 1
			setPhotos((prev) =>
				prev.map((p) =>
					p.url === item.url
						? {
								...p,
								edits,
								editedBlob: rendered?.blob,
								editedUrl,
								uploaded: null,
								uploading: true,
								uploadError: null,
								uploadToken: token,
							}
						: p,
				),
			)
			if (staleUpload) {
				api
					.destroyUpload(
						boxId,
						staleUpload.cloudinary_public_id,
						staleUpload.resource_type,
					)
					.catch(() => {})
			}
			uploadPhoto(rendered?.blob ?? item.file, item.url, token)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to edit photo")
		}
	}

	const startRecording = async (kind: "audio" | "video") => {
		setRecordingKind(kind)
		await recorder.start(kind)
	}

	const onRecordingFile = (e: Event) => {
		const input = e.target as HTMLInputElement
		const file = input.files?.[0]
		if (!file) return
		const kind: "audio" | "video" = file.type.startsWith("video/")
			? "video"
			: "audio"
		setRecording({
			file,
			url: URL.createObjectURL(file),
			kind,
			uploaded: null,
			uploading: true,
			uploadError: null,
		})
		input.value = ""
		// Eagerly upload the picked file.
		uploadRecording(file, kind)
	}

	const discardRecording = () => {
		if (recording) {
			URL.revokeObjectURL(recording.url)
			if (recording.uploaded) {
				api
					.destroyUpload(
						boxId,
						recording.uploaded.cloudinary_public_id,
						recording.uploaded.resource_type,
					)
					.catch((err) =>
						console.warn("Failed to clean up Cloudinary asset:", err),
					)
			}
		}
		setRecording(null)
	}

	// Shared save path for both buttons — validates, uploads the artwork, and
	// hands off to `onSaved` for whatever should happen next (navigate away,
	// or reset the form for another piece). Only the post-save step differs.
	const submit = async (onSaved: () => void) => {
		setError("")
		if (photos.length === 0) {
			setError("Add at least one photo.")
			return
		}
		// Make sure all assets have finished uploading before saving.
		const stillUploading =
			photos.some((p) => !p.uploaded) || (recording && !recording.uploaded)
		if (stillUploading) {
			setError("Assets are still uploading. Please wait a moment.")
			return
		}

		setSaving(true)
		try {
			// Everything is already on Cloudinary — photos upload on pick and
			// re-upload on edit, so `p.uploaded` is always the edited pixels.
			const assets: NewAssetInput[] = photos.map((p) => ({
				kind: "photo" as const,
				...p.uploaded!,
			}))
			if (recording?.uploaded) {
				assets.push({
					kind: "recording",
					...recording.uploaded,
				})
			}
			await api.createArtwork(boxId, {
				title: title.trim(),
				note: note.trim(),
				date: madeDate,
				assets,
			})
			onSaved()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to save artwork")
			setSaving(false)
		}
	}

	// Primary Save: navigate back to the box, same as before.
	const saveAndClose = (e: Event) => {
		e.preventDefault()
		submit(() => {
			savedRef.current = true
			navigate(`/boxes/${boxId}`)
		})
	}

	// Secondary "Save & add another": stay on the page and reset for the next
	// piece. Note this does NOT set `savedRef` — the form stays mounted and
	// any photos/recording added after this reset are new, unsaved uploads
	// that must still be destroyed by the unmount cleanup if abandoned.
	const saveAndAddAnother = (e: Event) => {
		e.preventDefault()
		submit(() => {
			// Revoke object URLs for the photos/recording we just saved (leak
			// guard) — but never call destroyUpload for them, since they now
			// belong to the artwork we just created. Only the local preview
			// URLs need cleanup; the Cloudinary assets must stay.
			for (const p of photos) {
				URL.revokeObjectURL(p.url)
				if (p.editedUrl) URL.revokeObjectURL(p.editedUrl)
			}
			if (recording) URL.revokeObjectURL(recording.url)
			setPhotos([])
			setRecording(null)
			setRecordingKind(null)
			setTitle("")
			setNote("")
			setEditingIdx(null)
			// Date is intentionally left as-is: batches being digitized usually
			// share an era, and it defaults to today anyway on a fresh visit.
			setSaving(false)
			toast("Saved", "success")
			formTopRef.current?.scrollIntoView({ behavior: "smooth" })
		})
	}

	const anyUploading = photos.some((p) => p.uploading) || recording?.uploading

	return (
		<div
			ref={formTopRef}
			class="container py-3 py-sm-4"
			style={{ maxWidth: "640px" }}
		>
			<div class="d-flex align-items-center justify-content-between mb-3">
				<h1 class="h4 mb-0 text-break">Add art to {box.box.name}</h1>
				<a
					href={`/boxes/${boxId}`}
					class="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center flex-shrink-0"
					style={{ minHeight: "44px" }}
				>
					Cancel
				</a>
			</div>

			<form onSubmit={saveAndClose} class="vstack gap-4">
				<div>
					<label class="form-label fw-semibold">Photos</label>
					{photos.length > 0 && (
						<div class="row row-cols-3 g-2 mb-2">
							{photos.map((p, idx) => (
								<div class="col" key={p.url}>
									<div class="ratio ratio-1x1 position-relative">
										<button
											type="button"
											class="p-0 border-0 bg-transparent w-100 h-100"
											onClick={() => !saving && setEditingIdx(idx)}
											aria-label="Edit photo"
										>
											<img
												src={p.editedUrl ?? p.url}
												alt=""
												class="w-100 h-100 object-fit-cover rounded"
											/>
										</button>
										{!p.uploadError && (
											<span
												class="position-absolute bottom-0 start-0 m-1 badge text-bg-dark bg-opacity-75 d-inline-flex align-items-center gap-1"
												aria-hidden="true"
											>
												<i class="bi bi-pencil-fill" />
												{!isNoopEdits(p.edits) && <span>Edited</span>}
											</span>
										)}
										{p.uploading && (
											<div class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25 rounded">
												<Spinner size={24} />
											</div>
										)}
										{p.uploadError && (
											<div class="position-absolute bottom-0 start-0 end-0 bg-danger text-white small text-center px-1">
												Upload failed
											</div>
										)}
										<button
											type="button"
											class="btn btn-danger position-absolute rounded-circle d-flex align-items-center justify-content-center"
											// Half-size remove button, nudged outside the thumbnail corner
											// so it doesn't cover the photo.
											style={{
												width: "22px",
												height: "22px",
												padding: 0,
												top: "-6px",
												right: "-6px",
											}}
											onClick={() => removePhoto(idx)}
											aria-label="Remove photo"
										>
											<i
												class="bi bi-x-lg"
												style={{ fontSize: "0.7rem" }}
												aria-hidden="true"
											/>
										</button>
									</div>
								</div>
							))}
						</div>
					)}
					<div class="row row-cols-2 g-2">
						<div class="col">
							<label class="btn btn-primary btn-lg w-100 d-inline-flex align-items-center justify-content-center gap-2 mb-0">
								<i class="bi bi-camera-fill" aria-hidden="true" />
								<span>Take photo</span>
								{/* capture="environment" makes mobile browsers open the
								    camera directly; keep it only on this input. */}
								<input
									type="file"
									accept="image/*"
									capture="environment"
									multiple
									class="d-none"
									onChange={onPhotoInput}
								/>
							</label>
						</div>
						<div class="col">
							<label class="btn btn-outline-primary btn-lg w-100 d-inline-flex align-items-center justify-content-center gap-2 mb-0">
								<i class="bi bi-images" aria-hidden="true" />
								<span>Choose photos</span>
								{/* No capture attribute: opens the photo gallery / file
								    picker instead of the camera. */}
								<input
									type="file"
									accept="image/*"
									multiple
									class="d-none"
									onChange={onPhotoInput}
								/>
							</label>
						</div>
					</div>
				</div>

				<div>
					<label class="form-label fw-semibold">Artist's demo (optional)</label>

					{!recording && !recorder.recording && (
						<div class="row row-cols-3 g-2">
							<div class="col">
								<button
									type="button"
									class="btn btn-outline-secondary w-100 py-3 d-flex flex-column align-items-center gap-1"
									onClick={() => startRecording("audio")}
								>
									<i class="bi bi-mic-fill fs-4" aria-hidden="true" />
									<span class="small">Record audio</span>
								</button>
							</div>
							<div class="col">
								<button
									type="button"
									class="btn btn-outline-secondary w-100 py-3 d-flex flex-column align-items-center gap-1"
									onClick={() => startRecording("video")}
								>
									<i class="bi bi-camera-video-fill fs-4" aria-hidden="true" />
									<span class="small">Record video</span>
								</button>
							</div>
							<div class="col">
								<label class="btn btn-outline-secondary w-100 py-3 d-flex flex-column align-items-center gap-1 mb-0">
									<i class="bi bi-folder2-open fs-4" aria-hidden="true" />
									<span class="small">Pick a file</span>
									<input
										type="file"
										accept="audio/*,video/*"
										class="d-none"
										onChange={onRecordingFile}
									/>
								</label>
							</div>
						</div>
					)}

					{recorder.error && (
						<div class="alert alert-danger mt-2">{recorder.error}</div>
					)}

					{recorder.recording && (
						<div class="mt-2">
							<div class="text-center">
								{recordingKind === "video" && (
									<LiveVideo stream={recorder.stream} />
								)}
								<div class="display-6 my-2">
									{String(Math.floor(recorder.elapsedSeconds / 60)).padStart(
										2,
										"0",
									)}
									:{String(recorder.elapsedSeconds % 60).padStart(2, "0")}
								</div>
								<button
									type="button"
									class="btn btn-danger btn-lg"
									onClick={() => recorder.stop()}
								>
									<i class="bi bi-stop-fill" aria-hidden="true" />
									<span>Stop</span>
								</button>
							</div>
						</div>
					)}

					{recording && (
						<div class="mt-2">
							<div>
								{recording.kind === "video" ? (
									<video
										src={recording.url}
										controls
										class="w-100 rounded bg-dark"
										style={{ maxHeight: "40vh" }}
									/>
								) : (
									<audio src={recording.url} controls class="w-100" />
								)}
								{recording.uploading && (
									<div class="d-flex align-items-center gap-2 mt-2 text-body-secondary">
										<Spinner size={16} />
										<span class="small">Uploading recording…</span>
									</div>
								)}
								{recording.uploadError && (
									<div class="text-danger small mt-1">
										Upload failed: {recording.uploadError}
									</div>
								)}
								<button
									type="button"
									class="btn btn-outline-danger mt-2 d-inline-flex align-items-center"
									style={{ minHeight: "44px" }}
									onClick={discardRecording}
								>
									<i class="bi bi-trash" aria-hidden="true" />
									<span>Discard</span>
								</button>
							</div>
						</div>
					)}
				</div>

				<div>
					<label class="form-label fw-semibold" for="made-year">
						Date made
					</label>
					<ArtDateInput
						idPrefix="made"
						value={madeDate}
						onChange={setMadeDate}
					/>
				</div>

				<div>
					<label class="form-label fw-semibold" for="title">
						Title (optional)
					</label>
					<input
						id="title"
						type="text"
						class="form-control"
						value={title}
						onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
					/>
				</div>

				<div>
					<label class="form-label fw-semibold" for="note">
						Note (optional)
					</label>
					<textarea
						id="note"
						class="form-control"
						rows={3}
						value={note}
						onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
					/>
				</div>

				{error && <div class="alert alert-danger">{error}</div>}

				<div class="d-flex flex-column flex-sm-row gap-2">
					<button
						type="submit"
						class="btn btn-success btn-lg flex-fill d-inline-flex align-items-center justify-content-center gap-2"
						disabled={saving || anyUploading}
					>
						{saving ? (
							<Spinner size={16} />
						) : (
							<i class="bi bi-check-lg" aria-hidden="true" />
						)}
						<span>
							{saving
								? "Saving…"
								: anyUploading
									? "Uploading…"
									: "Save artwork"}
						</span>
					</button>
					<button
						type="button"
						class="btn btn-outline-success btn-lg flex-fill d-inline-flex align-items-center justify-content-center gap-2"
						disabled={saving || anyUploading}
						onClick={saveAndAddAnother}
					>
						{saving ? (
							<Spinner size={16} />
						) : (
							<i class="bi bi-plus-lg" aria-hidden="true" />
						)}
						<span>Save & add another</span>
					</button>
				</div>
			</form>

			{editingIdx !== null && photos[editingIdx] && (
				<PhotoEditor
					file={photos[editingIdx].file}
					initialEdits={photos[editingIdx].edits}
					onApply={(edits) => applyEdits(photos[editingIdx], edits)}
					onCancel={() => setEditingIdx(null)}
				/>
			)}
		</div>
	)
}
